import type { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { NAV_KEYS, LOGO_SRC } from "@/mock";
import { useLanguage } from "@/i18n/LanguageContext";
import { resetConsent } from "@/lib/analytics";

const Icon = ({ children, label }: { children: ReactNode; label: string }) => (
  <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-label={label} role="img">
    {children}
  </svg>
);

const SOCIALS = [
  {
    name: "X",
    href: "https://x.com/thisisplays?s=11",
    d: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
  },
  {
    name: "Facebook",
    href: "https://www.facebook.com/share/1HYC2S5kUS/?mibextid=wwXIfr",
    d: "M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z",
  },
  {
    name: "Threads",
    href: "https://www.threads.com/@thisisplays?invite=0",
    d: "M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.781 3.631 2.695 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.964-.065-1.19.408-2.285 1.33-3.082.88-.76 2.119-1.207 3.583-1.291a13.853 13.853 0 013.02.142c-.126-.742-.375-1.332-.75-1.757-.513-.586-1.308-.883-2.359-.89h-.029c-.844 0-1.992.232-2.721 1.32l-1.696-1.137c.976-1.45 2.6-2.252 4.469-2.252h.045c3.122.02 4.982 1.94 5.176 5.32.111.046.22.094.328.145 1.525.717 2.642 1.803 3.23 3.142.819 1.866.896 4.91-1.74 7.495C18.013 23.025 15.95 23.973 12.186 24z",
  },
  {
    name: "TikTok",
    href: "https://www.tiktok.com/@thisisplays?_r=1&_t=ZG-97O1kP0TdR2",
    d: "M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z",
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/thisisplays?utm_source=qr",
    d: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z",
  },
  {
    name: "WhatsApp",
    href: "https://wa.me/351923321515",
    d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z",
  },
  {
    name: "YouTube",
    href: "https://youtube.com/@this_is_plays?si=2Hh9LY1FmaQRpsf_",
    d: "M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z",
  },
];

const Footer = () => {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const prefix = pathname === "/" ? "" : "/";
  const label = "text-xs font-bold tracking-[0.18em] uppercase text-white/40 mb-3";
  const link = "text-white/70 hover:text-[#F2641E] transition-colors text-sm";

  return (
    <footer data-testid="site-footer" className="bg-[#111] text-white pt-12 pb-6">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-8 text-center">
        <div className="flex flex-col items-center pb-8 border-b border-white/10">
          <img src={LOGO_SRC} alt="PLAYS logo" className="w-14 h-14 object-contain bg-white rounded-md p-1 mb-3" />
          <div className="font-archivo font-extrabold text-2xl leading-none text-center">PLAYS</div>
          <div className="text-[9px] font-semibold tracking-[0.2em] text-white/50 mt-1.5">PEOPLE · LINKED · AROUND · YOUR · SPORT</div>
          <div className={`${label} mt-6 mb-3`}>{t.footer.followUs}</div>
          <div className="flex flex-wrap justify-center gap-2.5">
            {SOCIALS.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.name}
                title={s.name}
                data-testid={`footer-social-${s.name.toLowerCase()}`}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-[#F2641E] text-white flex items-center justify-center transition-colors duration-200"
              >
                <Icon label={s.name}>
                  <path d={s.d} />
                </Icon>
              </a>
            ))}
          </div>
        </div>


        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 py-8 border-b border-white/10">
          <div>
            <div className={label}>{t.footer.navigate}</div>
            <ul className="space-y-2">
              {NAV_KEYS.map((l) => (
                <li key={l.key}>
                  <a href={`${prefix}${l.href}`} className={link}>
                    {t.nav[l.key]}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className={label}>{t.footer.contact}</div>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="mailto:support@playshub.net" data-testid="footer-email-link" className={link}>
                  support@playshub.net
                </a>
              </li>
              <li>
                <a href="mailto:services@playshub.net" className={link}>
                  services@playshub.net
                </a>
              </li>
              <li>
                <a href="https://wa.me/351923321515" target="_blank" rel="noopener noreferrer" className={link}>
                  +351 923 321 515
                </a>
              </li>
            </ul>
            <div className={`${label} mt-5 mb-2`}>{t.footer.languages}</div>
            <div className="text-white/70 text-sm">{t.footer.langsValue}</div>
          </div>
          <div>
            <div className={label}>{t.footer.legal}</div>
            <ul className="space-y-2">
              <li>
                <Link to="/privacidade" data-testid="footer-privacy-policy-link" className={link}>
                  {t.footer.privacy}
                </Link>
              </li>
              <li>
                <Link to="/termos" data-testid="footer-terms-of-use-link" className={link}>
                  {t.footer.terms}
                </Link>
              </li>
              <li>
                <button onClick={resetConsent} data-testid="footer-cookie-settings-button" className={link}>
                  {t.footer.cookies}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2.5 pt-6">
          <p className="text-white/45 text-xs max-w-2xl mx-auto leading-relaxed" data-testid="footer-trademark-notice">
            {t.footer.trademark}
          </p>
          <p className="text-white/40 text-xs" data-testid="footer-copyright">
            © {new Date().getFullYear()} {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
